/* Auto-generated from tiling-schemas.proto */
export namespace Tiling {
    /**
     * Type of tile.
     * Potential future types: ink, video, table etc.
     */
    export enum TileType {
        Unknown = 0,
        Text = 1,
        Slide = 2
    }
    /**
     * Define common metadata for tiles.
     */
    export interface ITileMetadata {
        /**
         * A value that changes when the tile content changes,
         * can be a hash for example.
         */
        revisionId?: string;
        /**
         * A montonically increasing sequence number that allows for chronological ordering
         * of tile content changes.
         * This number is not guaranteed to be unique per tile or tile content, nor are there
         * any guarantees about gaps in the ordering.
         */
        revisionSequence?: number;
        /**
         * Unique identifier for the tile within its top level container,
         * which is dependent on the context in which this tile is presented
         * (e.g. a ProofingRequest may contain a documentId that could be
         * prepended to this tileId to generate a globally unique identifier)
         */
        tileId?: string;
        /**
         * Tile primary type: text, video, image etc.
         */
        tileType?: TileType;
        /**
         * Document ID that is expected to be
         * globally unique. (Optional field.)
         */
        docId?: string;
        /**
         * ID of the session that is lost when document/session is closed.
         * For instance, the same document opened in different browser
         * tabs will have distinct session IDs. And different documents
         * opened in the same desktop application process session will
         * also have distinct session IDs. (Optional field.)
         */
        docSessionId?: string;
        /**
         * A value that indicates if this tile is a part of a reply or forward section of an email.
         * Otherwise it is a part of the original email or it is not part of an email.
         */
        replyOrForward?: boolean;
        /**
         * The file extension of the document (e.g. ".docx" for Word)
         */
        fileExtension?: string;
    }
    /**
     * Type of text tile.
     */
    export enum TextTileType {
        Generic = 0,
        Title = 1,
        SmartArt = 2,
        TableCell = 3,
        TextBox = 4,
        Notes = 5
    }
    /**
     * Text tile representation that can be used
     * for proofing, definitions, text analysis etc.
     */
    export interface ITextTile {
        /**
         * Common tile metadata such as tile ID, revision ID and tile type.
         */
        metadata?: ITileMetadata;
        /**
         * Non empty list of elements that make up the tile.
         */
        elements?: ITextTileElement[];
        /**
         * Text tile type which defaults to generic text.
         */
        subType?: TextTileType;
    }
    /**
     * Elements hold the actual text content of a text tile.
     * A text tile should contain at least 1 tile element.
     */
    export interface ITextTileElement {
        /**
         * Text content of the tile element.
         */
        text?: string;
        /**
         * Optional element text unit.
         */
        textUnit?: TextTileElementUnit;
        /**
         * Main text language, can be overridden
         * for specific spans of text.
         * e.g. "en-us", "fr-fr", "sr-ltn-cs"
         */
        languageId?: string;
        /**
         * Optional metadata for a tile element that is part of a list.
         */
        listMetadata?: IListMetadata;
        /**
         * Optional list of ranges in the tile element
         * for language and "do not process" settings overrides.
         */
        spans?: ITextTileElementSpan[];
        /**
         * isClean is used to indicate if the tile content has been modified or not.
         * isClean is a short term solution for saving COGS and latency for PPTO, as per current event model.
         * AL has long term solution to deprecated the field, replace with incremental tile model sync design between client and cloud.
         */
        isClean?: boolean;
    }
    /**
     * Text unit of a text tile element.
     */
    export enum TextTileElementUnit {
        Undefined = 0,
        Word = 1,
        Phrase = 2,
        Sentence = 3,
        Paragraph = 4
    }
    /**
     * Define an override range inside a tile element text.
     * Spans can overlap. For instance, a span that
     * marks a part of the text in a different language
     * can have a substring marked as "do not process".
     */
    export interface ITextTileElementSpan {
        /**
         * Do not process, check or analyze this span.
         */
        doNotProcess?: boolean;
        /**
         * Language ID override for this span.
         * e.g. "en-us", "fr-fr", "sr-ltn-cs"
         */
        languageId?: string;
        /**
         * Non zero span length.
         */
        length?: number;
        /**
         * Span start index starting from 0.
         */
        start?: number;
    }
    /**
     * Metadata describing the list properties of
     * a text element that is part of a list.
     */
    export interface IListMetadata {
        /**
         * Type of list: bullet or numbered.
         */
        listType?: ListType;
        /**
         * List level starting from 0.
         */
        listLevel?: number;
        /**
         * Indentation level of the paragraph starting from 0.
         */
        indentationLevel?: number;
        /**
         * Bullet character for a bulleted list.
         */
        listTag?: string;
        /**
         * Type of numeration for a numbered list.
         */
        listNumeration?: ListNumeration;
        /**
         * Index of this particular paragraph in the list.
         */
        listElementIndex?: number;
    }
    /**
     * Types of list.
     */
    export enum ListType {
        Undefined = 0,
        Bullet = 1,
        Numbered = 2
    }
    /**
     * Possible numerations for a numbered list.
     */
    export enum ListNumeration {
        Undefined = 0,
        AlphaLcParenBoth = 1,
        AlphaUcParenBoth = 2,
        AlphaLcParenR = 3,
        AlphaUcParenR = 4,
        AlphaLcPeriod = 5,
        AlphaUcPeriod = 6,
        ArabicParenBoth = 7,
        ArabicParenR = 8,
        ArabicPeriod = 9,
        ArabicPlain = 10,
        RomanLcParenBoth = 11,
        RomanUcParenBoth = 12,
        RomanLcParenR = 13,
        RomanUcParenR = 14,
        RomanLcPeriod = 15,
        RomanUcPeriod = 16,
        CircleNumDbPlain = 17,
        CircleNumWdBlackPlain = 18,
        CircleNumWdWhitePlain = 19,
        ArabicDbPeriod = 20,
        ArabicDbPlain = 21,
        Ea1ChsPeriod = 22,
        Ea1ChsPlain = 23,
        Ea1ChtPeriod = 24,
        Ea1ChtPlain = 25,
        Ea1JpnChsDbPeriod = 26,
        Ea1JpnKorPlain = 27,
        Ea1JpnKorPeriod = 28,
        Arabic1Minus = 29,
        Arabic2Minus = 30,
        Hebrew2Minus = 31,
        ThaiAlphaPeriod = 32,
        ThaiAlphaParenR = 33,
        ThaiAlphaParenBoth = 34,
        ThaiNumPeriod = 35,
        ThaiNumParenR = 36,
        ThaiNumParenBoth = 37,
        HindiAlphaPeriod = 38,
        HindiNumPeriod = 39,
        HindiNumParenR = 40,
        HindiAlpha1Period = 41
    }
    /**
     * Text tile event type corresponding to OnTileDeleted, OnTileAdded,
     * and OnTileChanged.
     */
    export enum TextTileEventType {
        Undefined = 0,
        Delete = 1,
        Add = 2,
        Change = 3,
        Refresh = 4
    }
    /**
     * Text tile event, including the event type and tile data.
     */
    export interface ITextTileEvent {
        type?: TextTileEventType;
        tile?: ITextTile;
    }
    /**
     * Batched Text tile event, including the event type and tile data.
     */
    export interface IBatchTextTileEvent {
        textTileEvents?: ITextTileEvent[];
        refreshEnabled?: boolean;
    }
}
